'use client';

import { useLayoutEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import {
  CustomAppMobileNav,
  CustomAppSidebarDesktop,
  EnterpriseSidebarLayoutProvider,
  useEnterpriseSidebarLayout,
} from '@/components/layout/custom-app-sidebar';
import { enterpriseSidebarContentStartClass } from '@/components/layout/enterprise-sidebar-dimensions';
import { getMobilePageTitle } from '@/lib/navigation/mobile-page-title';
import { isNavActive } from '@/lib/navigation/is-nav-active';
import { useRTLUtils } from '@/lib/translation';
import {
  MobileShellTopBar,
  MobileShellTopBarTrailing,
  mobileShellTopBarTitleClassName,
} from '@/components/layout/mobile-shell-top-bar';
import { cn } from '@/lib/utils';

/**
 * Custom-plan shell: persistent side navigation instead of the SaaS top navbar.
 * Routing matches `useAppNavItems` / main navbar; only layout and visual language differ.
 */
export function CustomAppChrome({
  banners,
  children,
}: {
  banners?: React.ReactNode;
  children: React.ReactNode;
}) {
  const pathname = usePathname() ?? '';
  const { t } = useTranslation();
  const { textAlignClass } = useRTLUtils();
  const mobileTitle = getMobilePageTitle(pathname, t);

  return (
    <EnterpriseSidebarLayoutProvider>
      <CustomAppChromeBody banners={banners} mobileTitle={mobileTitle} textAlignClass={textAlignClass}>
        {children}
      </CustomAppChromeBody>
    </EnterpriseSidebarLayoutProvider>
  );
}

function CustomAppChromeBody({
  banners,
  children,
  mobileTitle,
  textAlignClass,
}: {
  banners?: React.ReactNode;
  children: React.ReactNode;
  mobileTitle: string | null;
  textAlignClass: string;
}) {
  const pathname = usePathname() ?? '';
  const { pinMode, collapsed } = useEnterpriseSidebarLayout();
  const isChatsRoute = isNavActive(pathname, '/chats');
  const isTestAgentRoute = isNavActive(pathname, '/test-agent');
  const isImmersiveRoute = isChatsRoute || isTestAgentRoute;
  const mainScrollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    mainScrollRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  return (
    <div className="relative h-dvh min-h-0 w-full overflow-hidden print:block supports-[height:100dvh]:h-[100dvh]">
      <CustomAppSidebarDesktop />
      <div
        className={cn(
          /* Fixed to the viewport (same as the aside) with start + end — no padding lane. */
          'fixed top-0 bottom-0 end-0 z-0 flex min-h-0 flex-col overflow-hidden',
          'transition-[inset-inline-start] duration-150 ease-out',
          enterpriseSidebarContentStartClass(pinMode, collapsed),
        )}
      >
        <MobileShellTopBar
          menu={<CustomAppMobileNav />}
          title={
            <span className={mobileShellTopBarTitleClassName(textAlignClass)}>
              {mobileTitle ?? 'Reach'}
            </span>
          }
          trailing={<MobileShellTopBarTrailing theme="enterprise" />}
        />
        {banners ? <div className="shrink-0">{banners}</div> : null}
        <div
          ref={mainScrollRef}
          className={cn(
            'flex min-h-0 min-w-0 w-full flex-1 flex-col overscroll-y-contain bg-background',
            '[-webkit-overflow-scrolling:touch] pb-[env(safe-area-inset-bottom,0px)]',
            isImmersiveRoute ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden',
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
